// God mode shell: login gate, sidebar navigation (hash-routed so every page has its own
// URL and survives a refresh), top bar, and the page outlet. Pages live in ./pages and are
// built only from ./kit.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDownToLine, Bell, BookOpen, Code2, Gauge, KeyRound, LayoutGrid, LogOut, Menu, Send, ShieldCheck, Smartphone, Store, Webhook, X,
} from "lucide-react";
import Logo from "../components/Logo";
import AuthSplit from "../components/sc/AuthSplit";
import { getToken, god, onAuthError } from "./godApi";
import { KitStyles, NavContext, Pill } from "./kit";
import Overview from "./pages/Overview";
import Merchants from "./pages/Merchants";
import Devices from "./pages/Devices";
import Notifications from "./pages/Notifications";
import Parser from "./pages/Parser";
import GwOverview from "./pages/gateway/GwOverview";
import GwTxns from "./pages/gateway/GwTxns";
import GwMerchants from "./pages/gateway/GwMerchants";
import GwCallbacks from "./pages/gateway/GwCallbacks";
import GwLedger from "./pages/gateway/GwLedger";
import GwAccount from "./pages/gateway/GwAccount";

const NAV = [
  { group: "Monitor", items: [
    { path: "overview", label: "Overview", icon: LayoutGrid, sub: "Platform volume, health and versions at a glance.", el: Overview },
  ] },
  { group: "Gateway", items: [
    { path: "gateway", label: "Gateway", icon: Gauge, sub: "Auto Pay and payouts across every merchant, reconciled against ProtonPay.", el: GwOverview },
    { path: "gateway/payins", label: "Payins", icon: ArrowDownToLine, sub: "Every Auto Pay collection, live.", el: () => <GwTxns kind="payins" /> },
    { path: "gateway/payouts", label: "Payouts", icon: Send, sub: "Bank payouts from merchant balances — resolve anything held for review.", el: () => <GwTxns kind="payouts" /> },
    { path: "gateway/merchants", label: "Merchant access", icon: ShieldCheck, sub: "Switch Auto Pay and payouts on per merchant, set routing and limits, adjust balances.", el: GwMerchants },
    { path: "gateway/callbacks", label: "Callbacks", icon: Webhook, sub: "Bank callbacks received from ProtonPay and callbacks sent to merchants.", el: GwCallbacks },
    { path: "gateway/ledger", label: "Ledger", icon: BookOpen, sub: "Every balance movement across all merchants.", el: GwLedger },
    { path: "gateway/account", label: "ProtonPay account", icon: KeyRound, sub: "Credentials, environment, callback URLs and IP allowlist.", el: GwAccount },
  ] },
  { group: "Merchants", items: [
    { path: "merchants", label: "Merchants", icon: Store, sub: "Onboard, inspect and manage every merchant account.", el: Merchants },
  ] },
  { group: "Capture", items: [
    { path: "devices", label: "Devices", icon: Smartphone, sub: "Notification-capture phones across all merchants.", el: Devices },
    { path: "notifications", label: "Notifications", icon: Bell, sub: "Raw bank notifications from devices — coverage and parse results.", el: Notifications },
    { path: "parser", label: "Parser", icon: Code2, sub: "Rules that turn bank alerts into credits.", el: Parser },
  ] },
];
const ALL = NAV.flatMap((g) => g.items.map((i) => ({ ...i, group: g.group })));

const readHash = () => window.location.hash.replace(/^#\/?/, "") || "overview";

// Longest nav path that prefixes the current path ("merchants/abc" → Merchants).
function matchRoute(path) {
  return ALL.filter((r) => path === r.path || path.startsWith(r.path + "/"))
    .sort((a, b) => b.path.length - a.path.length)[0] || ALL[0];
}

export default function GodApp() {
  // Resume the tab's session after a refresh (token in sessionStorage, expiry-checked).
  // Any 401 anywhere drops straight back to login.
  const [authed, setAuthed] = useState(() => !!getToken());
  const [path, setPath] = useState(readHash);
  const [menu, setMenu] = useState(false);
  const [env, setEnv] = useState(null);

  useEffect(() => {
    onAuthError(() => { god.logout(); setAuthed(false); });
    const onHash = () => { setPath(readHash()); setMenu(false); };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);
  useEffect(() => {
    if (authed) god.gwSettings().then((s) => setEnv(s.configured ? s.environment : "off")).catch(() => {});
  }, [authed]);

  const go = useCallback((p) => { window.location.hash = "/" + p; }, []);
  const nav = useMemo(() => ({ path, go }), [path, go]);
  const route = matchRoute(path);
  const Page = route.el;

  if (!authed) return <Login onIn={() => setAuthed(true)} />;
  const logout = () => { god.logout(); setAuthed(false); };

  return (
    <NavContext.Provider value={nav}>
      <div className="gx">
        <KitStyles />
        <aside className={`gx-side ${menu ? "is-open" : ""}`}>
          <div className="gx-side-top">
            <a href="#/overview" className="gx-logo" aria-label="SuperCash"><Logo height={86} /></a>
            <button type="button" className="gx-close" onClick={() => setMenu(false)} aria-label="Close menu"><X size={18} /></button>
          </div>
          <nav className="gx-nav">
            {NAV.map((g) => (
              <div key={g.group} className="gx-group">
                <div className="gx-group-label">{g.group}</div>
                {g.items.map((it) => {
                  const on = it.path === route.path;
                  const Icon = it.icon;
                  return (
                    <a key={it.path} href={`#/${it.path}`} data-testid={`god-nav-${it.path.replace("/", "-")}`}
                      className={`gx-item ${on ? "is-on" : ""}`} aria-current={on ? "page" : undefined}>
                      <Icon size={17} strokeWidth={on ? 2.3 : 1.9} aria-hidden="true" />
                      <span>{it.label}</span>
                    </a>
                  );
                })}
              </div>
            ))}
          </nav>
          <div className="gx-side-foot">
            <button type="button" className="gx-logout" onClick={logout}><LogOut size={16} /> Log out</button>
          </div>
        </aside>
        {menu && <div className="gx-scrim" onClick={() => setMenu(false)} />}

        <div className="gx-body">
          <header className="gx-top">
            <button type="button" className="gx-burger" onClick={() => setMenu(true)} aria-label="Open menu"><Menu size={20} /></button>
            <div className="gx-title">
              <h1>{route.label}</h1>
              <p title={route.sub}>{route.sub}</p>
            </div>
            <div className="gx-top-right">
              {env && (
                <Pill tone={env === "live" ? "green" : env === "test" ? "amber" : "gray"} dot title="ProtonPay environment">
                  {env === "live" ? "Live" : env === "test" ? "Test mode" : "Gateway off"}
                </Pill>
              )}
            </div>
          </header>
          <main className="gx-main" key={route.path}>
            <Page />
          </main>
        </div>

        <style>{`
          .gx { min-height: 100vh; display: flex; background: #F5F6FA; color: #111827; font-family: "Inter", system-ui, sans-serif; }
          .gx-side { width: 248px; flex: none; position: sticky; top: 0; height: 100vh; background: #fff; border-right: 1px solid #E4E7EF; display: flex; flex-direction: column; box-sizing: border-box; z-index: 60; }
          .gx-side-top { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px 4px; }
          .gx-logo { height: 58px; display: flex; align-items: center; overflow: hidden; margin-left: -8px; }
          .gx-close { display: none; border: 0; background: none; color: #4b5263; cursor: pointer; padding: 6px; border-radius: 8px; }
          .gx-nav { flex: 1; overflow-y: auto; padding: 6px 12px 12px; display: flex; flex-direction: column; gap: 14px; }
          .gx-group { display: flex; flex-direction: column; gap: 2px; }
          .gx-group-label { font-size: 11px; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; color: #8A90A1; padding: 4px 10px 6px; }
          .gx-item { display: flex; align-items: center; gap: 11px; height: 36px; padding: 0 10px; border-radius: 9px; color: #4b5263; font-size: 14px; font-weight: 500; text-decoration: none; transition: background .12s, color .12s; }
          .gx-item:hover { background: #F3F4FA; color: #111827; }
          .gx-item.is-on { color: #fff; font-weight: 600; background: radial-gradient(120% 170% at 0% 0%, #4c4ff0 0%, #2f33d6 55%, #1c1aa0 100%); box-shadow: 0 6px 16px -9px rgba(47,51,214,.7); }
          .gx-side-foot { padding: 12px; border-top: 1px solid #EEF0F5; }
          .gx-logout { width: 100%; display: inline-flex; align-items: center; justify-content: center; gap: 8px; height: 38px; border: 1px solid #E4E7EF; border-radius: 10px; background: #fff; color: #4b5263; font: inherit; font-size: 13.5px; font-weight: 600; cursor: pointer; }
          .gx-logout:hover { color: #c5221f; border-color: #F3C9C5; background: #FDF6F5; }
          .gx-body { flex: 1; min-width: 0; display: flex; flex-direction: column; }
          .gx-top { position: sticky; top: 0; z-index: 40; display: flex; align-items: center; gap: 12px; height: 52px; padding: 0 24px; box-sizing: border-box; background: rgba(255,255,255,.9); backdrop-filter: blur(10px); border-bottom: 1px solid #E4E7EF; }
          .gx-burger { display: none; border: 1px solid #E4E7EF; background: #fff; border-radius: 9px; width: 34px; height: 34px; align-items: center; justify-content: center; color: #1f2430; cursor: pointer; flex: none; }
          .gx-title { flex: 1; min-width: 0; display: flex; align-items: baseline; gap: 12px; }
          .gx-title h1 { margin: 0; font-size: 16px; font-weight: 700; letter-spacing: -0.01em; color: #111827; white-space: nowrap; flex: none; }
          .gx-title p { margin: 0; font-size: 13px; color: #8A90A1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; min-width: 0; }
          .gx-top-right { display: flex; align-items: center; gap: 10px; flex: none; }
          .gx-main { padding: 18px 24px 64px; width: 100%; max-width: 1480px; box-sizing: border-box; }
          .gx-scrim { position: fixed; inset: 0; background: rgba(17,24,39,.32); z-index: 55; }
          @media (max-width: 980px) {
            .gx-side { position: fixed; left: 0; top: 0; bottom: 0; transform: translateX(-102%); transition: transform .18s ease; box-shadow: 12px 0 40px -20px rgba(17,24,39,.4); }
            .gx-side.is-open { transform: none; }
            .gx-close { display: inline-flex; }
            .gx-burger { display: inline-flex; }
            .gx-top { padding: 0 12px; }
            .gx-title h1 { font-size: 15px; }
            .gx-title p { display: none; }
            .gx-main { padding: 14px 12px 48px; }
          }
        `}</style>
      </div>
    </NavContext.Provider>
  );
}

function Login({ onIn }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await god.login(email.trim(), password); onIn(); }
    catch (x) { setErr(x.message || "Login failed"); }
    finally { setBusy(false); }
  };
  return (
    <AuthSplit
      headline={<>Hello<br />SuperCash! <span className="gl-wave">👋</span></>}
      blurb="Every merchant, payment, payout and device in one place. Run the platform, settle the gateway and keep money moving."
      title="Welcome Back!"
      subtitle="Restricted area — superadmin accounts only."
      onSubmit={submit}
      footer={<p className="gl-help">Forgot password? Ask another superadmin to reset it.</p>}
    >
      <input className="gl-input" type="email" placeholder="Email" aria-label="Email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" autoFocus />
      <input className="gl-input" type="password" placeholder="Password" aria-label="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
      {err && <div className="gl-err" role="alert">{err}</div>}
      <button className="gl-btn" disabled={busy}>{busy ? "Signing in…" : "Login Now"}</button>
    </AuthSplit>
  );
}
